import React, { useState, useEffect } from 'react'
import { 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  CircularProgress, 
  Snackbar,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  TextField
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { 
  Error as ErrorIcon, 
  TrendingUp, 
  TrendingDown,
  Equalizer,
  AttachMoney,
  Payment,
  CalendarToday,
  DateRange,
  Receipt
} from '@material-ui/icons'
import CountUp from 'react-countup'
import bilanClientService from '../../services/BilanClientService'

const useStyles = makeStyles((theme) => ({
  card: {
    height: '100%',
    transition: 'transform 0.3s, box-shadow 0.3s',
    '&:hover': {
      transform: 'translateY(-5px)',
      boxShadow: theme.shadows[4]
    }
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '200px'
  },
  errorContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
    margin: theme.spacing(2),
    backgroundColor: '#ffebee',
    borderRadius: theme.shape.borderRadius,
    border: '1px solid #f44336'
  },
  errorIcon: {
    marginRight: theme.spacing(1),
    color: '#f44336'
  },
  positive: {
    color: '#4CAF50',
    fontWeight: 'bold'
  },
  negative: {
    color: '#f44336',
    fontWeight: 'bold'
  },
  neutral: {
    color: '#FF9800',
    fontWeight: 'bold'
  },
  statCard: {
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    padding: theme.spacing(2)
  },
  productTable: {
    marginTop: theme.spacing(2)
  },
  tableHeader: {
    backgroundColor: theme.palette.primary.main,
    '& th': {
      color: 'white',
      fontWeight: 'bold'
    }
  },
  chip: {
    margin: theme.spacing(0.5)
  },
  select: {
    marginBottom: theme.spacing(2),
    minWidth: 200
  },
  clientCard: {
    borderLeft: '4px solid #FFD700',
    background: 'linear-gradient(135deg, #fff9c4 0%, #fffde7 100%)'
  },
  topClient: {
    background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
    border: '2px solid #ff9800',
    position: 'relative',
    overflow: 'visible'
  },
  avatar: {
    backgroundColor: '#ff9800',
    width: theme.spacing(6),
    height: theme.spacing(6)
  },
  rankBadge: {
    position: 'absolute',
    top: -10,
    right: -10,
    backgroundColor: '#ff5722',
    color: 'white',
    borderRadius: '50%',
    width: 30,
    height: 30,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    fontSize: '0.8rem',
    zIndex: 1
  },
  goldBadge: {
    backgroundColor: '#FFD700',
    color: '#000'
  },
  silverBadge: {
    backgroundColor: '#C0C0C0',
    color: '#000'
  },
  bronzeBadge: {
    backgroundColor: '#CD7F32',
    color: '#FFF'
  },
  tabs: {
    marginBottom: theme.spacing(3),
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  clientStats: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(1)
  },
  paymentStatus: {
    fontSize: '0.75rem',
    padding: '2px 6px',
    borderRadius: '4px'
  },
  paid: {
    backgroundColor: '#4CAF50',
    color: 'white'
  },
  unpaid: {
    backgroundColor: '#f44336',
    color: 'white'
  },
  partial: {
    backgroundColor: '#FF9800',
    color: 'white'
  },
  periodeCard: {
    background: 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)',
    border: '1px solid #e0e0e0'
  },
  montant: {
    fontWeight: 'bold'
  }
}))

function BilanClient() {
  const classes = useStyles()
  const [bilanData, setBilanData] = useState(null)
  const [quotidienData, setQuotidienData] = useState(null)
  const [mensuelData, setMensuelData] = useState(null)
  const [impayesData, setImpayesData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [periode, setPeriode] = useState('jour')
  const [typeBilan, setTypeBilan] = useState('general')
  const [activeTab, setActiveTab] = useState(0)
  const [datePersonnalisee, setDatePersonnalisee] = useState('')

  const periodes = [
    { key: 'jour', label: "Aujourd'hui", icon: <CalendarToday /> },
    { key: 'hier', label: 'Hier', icon: <CalendarToday /> },
    { key: 'mois', label: 'Mois en cours', icon: <DateRange /> },
    { key: 'annee', label: 'Année en cours', icon: <DateRange /> },
  ]

  const typesBilan = [
    { key: 'general', label: 'Bilan Général', icon: <Equalizer /> },
    { key: 'quotidien', label: 'Quotidien', icon: <CalendarToday /> },
    { key: 'mensuel', label: 'Mensuel', icon: <DateRange /> },
    { key: 'impayes', label: 'Impayés', icon: <Receipt /> }
  ]


  // Fonction pour formater les dates
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  
  try {
    const date = new Date(dateString);
    
    // Vérifier si la date est valide
    if (isNaN(date.getTime())) {
      return dateString; // Retourner la chaîne originale si parsing échoue
    }
    
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch (error) {
    console.error('Erreur formatage date:', error);
    return dateString;
  }
};

// Fonction pour formater les dates avec heure (si nécessaire)
const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  
  try {
    const date = new Date(dateString);
    
    if (isNaN(date.getTime())) {
      return dateString;
    }
    
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    console.error('Erreur formatage datetime:', error);
    return dateString;
  }
};

  useEffect(() => {
    fetchData()
  }, [periode, typeBilan, activeTab, datePersonnalisee])

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)

      console.log('🔄 Chargement des données...', { activeTab, typeBilan, periode, datePersonnalisee })

      let response

      switch (typeBilan) {
        case 'general':
          const params = periode === 'personnalise' && datePersonnalisee 
            ? { periode: 'personnalise', date: datePersonnalisee }
            : { periode }
          response = await bilanClientService.getBilanGeneral(params)
          setBilanData(response.data)
          setQuotidienData(null)
          setMensuelData(null)
          setImpayesData(null)
          break

        case 'quotidien':
          response = await bilanClientService.getBilanQuotidien(datePersonnalisee || undefined)
          setQuotidienData(response.data)
          setBilanData(null)
          setMensuelData(null)
          setImpayesData(null)
          break

        case 'mensuel':
          // Extraire année et mois de la date personnalisée ou utiliser la date actuelle
          const date = datePersonnalisee ? new Date(datePersonnalisee) : new Date()
          const annee = date.getFullYear()
          const mois = date.getMonth() + 1
          response = await bilanClientService.getBilanMensuel(annee, mois)
          setMensuelData(response.data)
          setBilanData(null)
          setQuotidienData(null)
          setImpayesData(null)
          break

        case 'impayes':
          const paramsImpayes = datePersonnalisee ? { date: datePersonnalisee } : {}
          response = await bilanClientService.getImpayesDetaille(paramsImpayes)
          setImpayesData(response.data)
          setBilanData(null)
          setQuotidienData(null)
          setMensuelData(null)
          break

        default:
          break
      }

    } catch (error) {
      console.error('❌ Erreur:', error)
      setError('Erreur lors du chargement du bilan')
      setSnackbarOpen(true)
    } finally {
      setLoading(false)
    }
  }

  // Composant pour formater les montants avec CountUp
const FormattedMontant = ({ montant, className = '', positive = false, negative = false }) => {
  const colorClass = positive ? classes.positive : negative ? classes.negative : '';
  
  // Convertir en nombre si c'est une chaîne
  const numericMontant = typeof montant === 'string' ? 
    parseFloat(montant.replace(/[^\d.-]/g, '')) : 
    Number(montant || 0);

  return (
    <CountUp 
      start={0} 
      end={numericMontant} 
      duration={1.5} 
      separator=" " 
      decimals={0}
      decimal=","
      suffix=" F CFA"
      className={`${className} ${colorClass} ${classes.montant}`}
    />
  );
};

  const formatPourcentage = (valeur) => {
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(valeur || 0)
  }

  const getTrendIcon = (valeur) => {
    if (valeur > 0) {
      return <TrendingUp className={classes.positive} />
    } else if (valeur < 0) {
      return <TrendingDown className={classes.negative} />
    }
    return <Equalizer />
  }

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false)
  }

  const handleRetry = () => {
    fetchData()
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
  }

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
        <Typography variant="body1" style={{ marginLeft: '10px' }}>
          Chargement des données...
        </Typography>
      </div>
    )
  }

  return (
    <div style={{ padding: '20px' }}>
      <Typography variant="h5" gutterBottom style={{ marginBottom: '30px', color: 'gray' }}>
        📊 Bilan Financier - Tableau de Bord
      </Typography>

      {/* Sélecteurs */}
      <Grid container spacing={2} alignItems="center" style={{ marginBottom: '20px' }}>
        <Grid item>
          <FormControl className={classes.select}>
            <InputLabel>Type de bilan</InputLabel>
            <Select
              value={typeBilan}
              onChange={(e) => setTypeBilan(e.target.value)}
            >
              {typesBilan.map((type) => (
                <MenuItem key={type.key} value={type.key}>
                  <Box display="flex" alignItems="center">
                    {type.icon}
                    <Box ml={1}>{type.label}</Box>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item>
          <FormControl className={classes.select}>
            <InputLabel>Période</InputLabel>
            <Select
              value={periode}
              onChange={(e) => setPeriode(e.target.value)}
            >
              {periodes.map((p) => (
                <MenuItem key={p.key} value={p.key}>
                  <Box display="flex" alignItems="center">
                    {p.icon}
                    <Box ml={1}>{p.label}</Box>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {(periode === 'personnalise' || typeBilan === 'quotidien') && (
          <Grid item>
          <FormControl className={classes.select} fullWidth>
            <InputLabel shrink htmlFor="date-personnalisee">
              Date
            </InputLabel>
            <TextField
              id="date-personnalisee"
              type="date"
              value={datePersonnalisee}
              onChange={(e) => setDatePersonnalisee(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                style: {
                  padding: '12px 8px',
                  fontSize: '14px'
                }
              }}
              variant="outlined"
              size="small"
            />
          </FormControl>
        </Grid>
        )}
      </Grid>

      {error && (
        <div className={classes.errorContainer}>
          <ErrorIcon className={classes.errorIcon} />
          <Typography variant="body1" style={{ flex: 1 }}>
            {error}
          </Typography>
          <Button color="secondary" onClick={handleRetry}>
            Réessayer
          </Button>
        </div>
      )}

      {/* Bilan Général */}
      {typeBilan === 'general' && bilanData && (
        <Grid container spacing={3}>
          {/* Cartes principales */}
          <Grid item xs={12} md={3}>
            <Card className={classes.statCard}>
              <CardContent>
                <AttachMoney style={{ fontSize: 40, color: '#4CAF50', marginBottom: '10px' }} />
                <Typography variant="h6" color="textSecondary">
                  Chiffre d'affaires
                </Typography>
                <Typography variant="h4">
                  <FormattedMontant montant={bilanData.chiffre_affaires?.total} positive />
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {bilanData.chiffre_affaires?.nombre_ventes} ventes
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card className={classes.statCard}>
              <CardContent>
                <TrendingUp style={{ fontSize: 40, color: '#2196F3', marginBottom: '10px' }} />
                <Typography variant="h6" color="textSecondary">
                  Bénéfices bruts
                </Typography>
                <Typography variant="h4">
                  <FormattedMontant montant={bilanData.benefices?.brut} positive />
                </Typography>
                <Typography variant="body2" color="textSecondary">
                <FormattedMontant montant={(bilanData.benefices?.quantite_vendue)} positive /> unités

                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card className={classes.statCard}>
              <CardContent>
                <Payment style={{ fontSize: 40, color: '#4CAF50', marginBottom: '10px' }} />
                <Typography variant="h6" color="textSecondary">
                  Bons payés
                </Typography>
                <Typography variant="h4">
                  <FormattedMontant montant={bilanData.paiements?.total_payes} positive />
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {bilanData.paiements?.nombre_payes} bons
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card className={classes.statCard}>
              <CardContent>
                <Receipt style={{ fontSize: 40, color: '#f44336', marginBottom: '10px' }} />
                <Typography variant="h6" color="textSecondary">
                  Impayés
                </Typography>
                <Typography variant="h4">
                  <FormattedMontant montant={bilanData.paiements?.total_impayes} negative />
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {bilanData.paiements?.nombre_impayes} bons
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Période */}
          <Grid item xs={12}>
            <Card className={classes.periodeCard}>
              <CardContent>
                <Typography variant="h6">
                📅 Période : {formatDate(bilanData.periode?.dateDebut)} au {formatDate(bilanData.periode?.dateFin)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Ventes par produit */}
          {bilanData.ventes_par_produit && bilanData.ventes_par_produit.length > 0 && (
            <Grid item xs={12} md={6}>
              <Card className={classes.card}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    🏆 Produits les plus vendus
                  </Typography>
                  <TableContainer component={Paper} className={classes.productTable}>
                    <Table size="small">
                      <TableHead className={classes.tableHeader}>
                        <TableRow>
                          <TableCell>Produit</TableCell>
                          <TableCell align="right">Quantité</TableCell>
                          <TableCell align="right">CA</TableCell>
                          <TableCell align="right">Bénéfice</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {bilanData.ventes_par_produit.slice(0, 5).map((produit, index) => (
                          <TableRow key={index}>
                            <TableCell>{produit.nomProd}</TableCell>
                            <TableCell align="right">{produit.quantite_vendue}</TableCell>
                            <TableCell align="right">
                              <FormattedMontant montant={produit.chiffre_affaires_produit} />
                            </TableCell>
                            <TableCell align="right">
                              <FormattedMontant montant={produit.benefice_produit} positive />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Statistiques clients */}
          {bilanData.statistiques_clients && (
            <Grid item xs={12} md={6}>
              <Card className={classes.card}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    👥 Statistiques clients
                  </Typography>
                  <Typography variant="body2">
                    • Nombre total de clients: {bilanData.statistiques_clients.length}
                  </Typography>
                  <Typography variant="body2">
                    • CA moyen par client: <FormattedMontant 
                      montant={
                        bilanData.statistiques_clients.reduce((sum, client) => sum + client.total_achats, 0) / 
                        bilanData.statistiques_clients.length
                      } 
                    />
                  </Typography>
                  <Typography variant="body2">
                    • Client le plus actif: {bilanData.statistiques_clients[0]?.nom}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Détail des impayés */}
          {bilanData.paiements?.detail_impayes && bilanData.paiements.detail_impayes.length > 0 && (
            <Grid item xs={12}>
              <Card className={classes.card}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    📋 Détail des impayés par client
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead className={classes.tableHeader}>
                        <TableRow>
                          <TableCell>Client</TableCell>
                          <TableCell align="right">Montant impayé</TableCell>
                          <TableCell align="right">Nombre de bons</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {bilanData.paiements.detail_impayes.map((impaye, index) => (
                          <TableRow key={index}>
                            <TableCell>{impaye.client_nom}</TableCell>
                            <TableCell align="right">
                              <FormattedMontant montant={impaye.total_impayes} negative />
                            </TableCell>
                            <TableCell align="right">{impaye.nombre_impayes}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      {/* Bilan Quotidien */}
      {typeBilan === 'quotidien' && quotidienData && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card className={classes.periodeCard}>
              <CardContent>
                <Typography variant="h6">
                📊 Bilan Quotidien - {formatDate(quotidienData.date) || 'Aujourd\'hui'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card className={classes.statCard}>
              <CardContent>
                <Typography variant="h6" color="textSecondary">
                  Chiffre d'affaires
                </Typography>
                <Typography variant="h4">
                  <FormattedMontant montant={quotidienData.chiffre_affaires} positive />
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card className={classes.statCard}>
              <CardContent>
                <Typography variant="h6" color="textSecondary">
                  Bénéfices
                </Typography>
                <Typography variant="h4">
                  <FormattedMontant montant={quotidienData.benefices} positive />
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card className={classes.statCard}>
              <CardContent>
                <Typography variant="h6" color="textSecondary">
                  Transactions
                </Typography>
                <Typography variant="h4">
                  {quotidienData.nombre_transactions}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card className={classes.statCard}>
              <CardContent>
                <Typography variant="h6" color="textSecondary">
                  Clients
                </Typography>
                <Typography variant="h4">
                  {quotidienData.nombre_clients}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Bilan Mensuel */}
      {typeBilan === 'mensuel' && mensuelData && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card className={classes.periodeCard}>
              <CardContent>
                <Typography variant="h6">
                  📈 Bilan Mensuel - {mensuelData.periode?.mois}/{mensuelData.periode?.annee}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Totaux */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Totaux du mois
                </Typography>
                <Typography variant="body2">
                  CA Total: <FormattedMontant montant={mensuelData.totaux?.chiffre_affaires} positive />
                </Typography>
                <Typography variant="body2">
                  Bénéfices: <FormattedMontant montant={mensuelData.totaux?.benefices} positive />
                </Typography>
                <Typography variant="body2">
                  Ventes: <strong>{mensuelData.totaux?.nb_ventes}</strong>
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Détails par jour */}
          {mensuelData.details_par_jour && (
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Évolution quotidienne
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead className={classes.tableHeader}>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell align="right">Ventes</TableCell>
                          <TableCell align="right">CA</TableCell>
                          <TableCell align="right">Bénéfices</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {mensuelData.details_par_jour.map((jour, index) => (
                          <TableRow key={index}>
                            <TableCell>{formatDate(jour.date)}</TableCell>
                            <TableCell align="right">{jour.nb_ventes}</TableCell>
                            <TableCell align="right">
                              <FormattedMontant montant={jour.chiffre_affaires} />
                            </TableCell>
                            <TableCell align="right">
                              <FormattedMontant montant={jour.benefices} positive />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      {/* Impayés détaillés */}
      {typeBilan === 'impayes' && impayesData && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card className={classes.periodeCard}>
              <CardContent>
                <Typography variant="h6">
                  📋 Détail des impayés
                </Typography>
                <Typography variant="body2">
                Total impayés: <FormattedMontant montant={impayesData.total_impayes} negative />
                sur <strong>{impayesData.nombre_impayes}</strong> bons
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {impayesData.details && impayesData.details.length > 0 ? (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Liste des impayés
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead className={classes.tableHeader}>
                        <TableRow>
                          <TableCell>Client</TableCell>
                          <TableCell>Produit</TableCell>
                          <TableCell align="right">Quantité</TableCell>
                          <TableCell align="right">Prix unitaire</TableCell>
                          <TableCell align="right">Total</TableCell>
                          <TableCell align="right">Date</TableCell>
                          <TableCell align="right">Facture</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {impayesData.details.map((impaye, index) => (
                          <TableRow key={index}>
                            <TableCell>{impaye.client}</TableCell>
                            <TableCell>{impaye.nomProd}</TableCell>
                            <TableCell align="right">{impaye.qtVdu}</TableCell>
                            <TableCell align="right">
                              <FormattedMontant montant={impaye.prixProdVendu} />
                            </TableCell>
                            <TableCell align="right">
                              <FormattedMontant montant={impaye.total} negative />
                            </TableCell>
                            <TableCell align="right">
                             {formatDate(impaye.dateEnreg)}
                            </TableCell>
                            <TableCell align="right">{impaye.numFactSrt}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          ) : (
            <Grid item xs={12}>
              <Card>
                <CardContent style={{ textAlign: 'center', padding: '40px' }}>
                  <Typography variant="h6" color="textSecondary">
                    Aucun impayé trouvé
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      {/* Message si pas de données */}
      {!bilanData && !quotidienData && !mensuelData && !impayesData && !loading && (
        <Grid item xs={12}>
          <Card>
            <CardContent style={{ textAlign: 'center', padding: '40px' }}>
              <Typography variant="h6" color="textSecondary">
                Aucune donnée disponible
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Les données pour cette période sont actuellement vides.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message={error}
        action={
          <Button color="inherit" size="small" onClick={handleCloseSnackbar}>
            Fermer
          </Button>
        }
      />
    </div>
  )
}

export default BilanClient